/**
 * ro-source.js — window.RoSource: turn ANY picked video into a source the whole
 * /remove-object-from-video pipeline can read.
 *
 * The pipeline has two readers of the file, and both must be satisfied:
 *   • the <video> element — plate sampling, tracking, the scene on screen;
 *   • WebCodecsFrameExtractor (mp4box + VideoDecoder) — the export, frame by
 *     frame at full rate. It falls back to <video>-seeking when it cannot
 *     demux the container, which works but decodes one seek at a time.
 *
 * What the measurements said (tests/scratch load bench, Chrome 151, 45 files):
 *   • H.264 in AVI / TS / FLV / WMV, MPEG-2, XVID: <video> → `error` (code 4).
 *   • HEVC on a machine WITHOUT a hardware decoder, MPEG-4 Part 2, MJPEG,
 *     ProRes, H.263: <video> fires `loadedmetadata` with videoWidth === 0 —
 *     NO error at all. The old loader took that as success and built a 16×16
 *     scene out of nothing. A hard `error` on a plain .MP4 is what the same
 *     codecs produce when the file has no audio track ("no supported streams").
 *     So the gate is DIMENSIONS, never the error event.
 *   • MKV / WebM decode natively but the hardware extractor cannot demux them;
 *     a `-c:v copy` remux to mp4 costs ~1 s and gives it back.
 *
 * Ladder (each rung is taken only when the one above failed for real):
 *   0  durable copy of the pick (FileBytes.settle) — the iOS Photos picker hands
 *      out a temporary file whose handle can die mid-read;
 *   1  <video> in the document, bounded wait for metadata, dims > 0;
 *   2  UniversalVideoDecode: remux (container problem) or transcode (codec
 *      problem) into H.264/AAC mp4, at crf 18 — this proxy is what the user's
 *      output is rendered from, so it is not the playback-copy quality point;
 *   2b native plays but the extractor cannot demux → copy remux, kept only if
 *      it verifies; the native source stays on any failure.
 *
 * Nothing here is tool-specific except the progress vocabulary, so the sibling
 * (/remove-text-from-video) can adopt it as-is.
 *
 *   const src = await RoSource.open(file, {
 *       video,                    // the tool's <video>; moved into a 1×1 visible host
 *       timeoutMs, ffmpegUrl, ladderUrl, codecMatrixUrl,
 *       onProgress(stage, frac)   // stage ∈ probe|engine|remux|convert|work
 *   });
 *   // → { file, url, orig, tier, vw, vh, dur, durable, vcodec, acodec,
 *   //     container, extractor, scaledTo, ms }
 *   // throws Error with .step ∈ error|timeout|dims|engine|unreadable|no_video|
 *   //   ladder|cancel, plus .mediaCode / .tier / .vcodec / .container when known
 *   RoSource.cancel();            // aborts a running rung 2 (FFmpeg is terminated)
 *   RoSource.ensureFfmpeg(cfg);   // the one engine loader — stub-aware, see below
 *   RoSource.release(src);        // revokes URLs of a source that is no longer used
 */
(function (root) {
    'use strict';
    if (root.RoSource) return;

    var host = null;
    function mediaHost() {
        if (host && host.isConnected) return host;
        host = document.createElement('div');
        host.setAttribute('aria-hidden', 'true');
        // 1×1, opacity 0, IN the document. Not display:none and not left:-9999px:
        // a detached or display-less element sleeps on iOS (readyState stuck at
        // 1, neither canplay nor error), and a negative offset breaks RTL pages.
        host.style.cssText = 'position:absolute;width:1px;height:1px;overflow:hidden;opacity:0;pointer-events:none';
        (document.querySelector('.ro-wrap') || document.body).appendChild(host);
        return host;
    }
    function attach(el) { if (el.parentNode !== mediaHost()) mediaHost().appendChild(el); }

    function loadScript(url) {
        return new Promise(function (res, rej) {
            var s = document.createElement('script');
            s.src = url; s.onload = res;
            s.onerror = function () { rej(new Error('script-load-failed: ' + url)); };
            document.head.appendChild(s);
        });
    }

    /* Bounded wait for metadata: "did not arrive" is a diagnosis (readyState,
       networkState), not an eternity. */
    function awaitMeta(el, ms) {
        return new Promise(function (res, rej) {
            var done = false, tm;
            function fin(ok, err) {
                if (done) return; done = true; clearTimeout(tm);
                el.removeEventListener('loadedmetadata', onMeta); el.removeEventListener('error', onErr);
                ok ? res() : rej(err);
            }
            function onMeta() { fin(true); }
            function onErr() {
                var e = new Error('video load failed'); e.step = 'error';
                e.mediaCode = el.error && el.error.code;
                e.mediaMsg = el.error && el.error.message;
                fin(false, e);
            }
            if (el.readyState >= 1) { fin(true); return; }
            el.addEventListener('loadedmetadata', onMeta); el.addEventListener('error', onErr);
            tm = setTimeout(function () {
                var e = new Error('video load timeout'); e.step = 'timeout';
                e.rs = el.readyState; e.ns = el.networkState; fin(false, e);
            }, ms);
        });
    }

    async function durable(file) {
        if (!(root.FileBytes && typeof root.FileBytes.settle === 'function')) return file;
        try { return (await root.FileBytes.settle(file)) || file; } catch (e) { return file; }
    }

    function revoke(url) { try { if (url) URL.revokeObjectURL(url); } catch (e) { } }
    function stem(name) { return String(name || 'video').replace(/\.[^.]+$/, ''); }
    function ext(name) { var m = /\.([a-z0-9]+)$/i.exec(String(name || '')); return m ? m[1].toLowerCase() : ''; }

    /* rung 1 — the element itself, with the dimension gate. */
    async function tryVideo(video, url, timeoutMs) {
        attach(video);
        video.src = url;
        if (video.load) video.load();
        await awaitMeta(video, timeoutMs);
        if (!video.videoWidth || !video.videoHeight) {
            var e = new Error('no video dimensions'); e.step = 'dims'; throw e;
        }
        return { vw: video.videoWidth, vh: video.videoHeight, dur: video.duration || 0 };
    }

    var cancelled = false;
    function throwIfCancelled() {
        if (!cancelled) return;
        var e = new Error('cancelled'); e.step = 'cancel'; throw e;
    }

    /* The layout registers QUEUE STUBS for shared modules on every page
       (`__sharedModule`): `window.FFmpegProcessor` exists before the script
       is fetched and a call on the stub waits 15 s for the real module, then
       fails with "module never loaded". So "is the module here?" must be
       asked as "is it here and not the stub" — the ladder guard caught this on
       its first run: every rung that needed the engine died on that timeout. */
    function real(mod) { return !!(mod && !mod.__isStub); }

    async function ensureFfmpeg(cfg) {
        if (!real(root.FFmpegProcessor) && cfg.ffmpegUrl) {
            try { await loadScript(cfg.ffmpegUrl); } catch (e) { /* judged below */ }
        }
        if (!real(root.FFmpegProcessor)) { var e = new Error('ffmpeg unavailable'); e.step = 'engine'; throw e; }
        if (!root.FFmpegProcessor.isReady()) {
            var ok = await root.FFmpegProcessor.init();
            if (ok === false || !root.FFmpegProcessor.isReady()) { var e2 = new Error('ffmpeg init failed'); e2.step = 'engine'; throw e2; }
        }
    }
    async function ensureLadder(cfg) {
        // codec-matrix is optional for the ladder but decides whether HEVC/AV1
        // are remuxable on THIS browser; ask for it first, tolerate its absence.
        if (!real(root.CodecMatrix) && cfg.codecMatrixUrl) { try { await loadScript(cfg.codecMatrixUrl); } catch (e) { } }
        if (!real(root.UniversalVideoDecode) && cfg.ladderUrl) { await loadScript(cfg.ladderUrl); }
        if (!real(root.UniversalVideoDecode)) { var e = new Error('decode ladder unavailable'); e.step = 'engine'; throw e; }
    }

    async function extractorVerdict(file) {
        var X = root.WebCodecsFrameExtractor;
        if (!X || typeof X.isSupported !== 'function') return { ok: false, reason: 'no-module', remuxable: false };
        try {
            var cap = await X.isSupported(file);
            var hasDecoder = (typeof VideoDecoder !== 'undefined');
            return {
                ok: !!(cap && cap.ok),
                reason: cap ? (cap.ok ? 'ok' : cap.reason) : 'unknown',
                // Only the container stands between this file and hardware
                // extraction; a codec the decoder refuses stays refused after a copy.
                remuxable: !!(cap && !cap.ok && cap.reason === 'container-unsupported' && hasDecoder)
            };
        } catch (e) { return { ok: false, reason: 'threw', remuxable: false }; }
    }

    /* rung 2 — UniversalVideoDecode; returns {file, url, tier, meta} or throws. */
    async function ladder(src, cfg, want, report) {
        await ensureLadder(cfg);
        throwIfCancelled();
        report('engine', 0.05);
        await ensureFfmpeg(cfg);
        throwIfCancelled();
        var res = await root.UniversalVideoDecode.open(src, {
            forceRemux: true,                       // rung 1 already proved native fails (or lacks the extractor)
            deferTranscode: !!want.remuxOnly,
            // the proxy is what the tool READS, never what it delivers — without
            // this the page counted it as the tool's result (phantom tool_complete)
            intermediate: true,
            // The caller may size the proxy for ITS use: /remove-object renders the
            // user's output from it (crf 18, full width), /audio-to-video only
            // paints it as a cover-fit background and passes { maxWidth: 1280 }.
            transcode: want.remuxOnly ? undefined
                : Object.assign({ crf: 18, preset: 'ultrafast', maxWidth: 1920 }, cfg.transcode || {}),
            onProgress: function (stage, frac) {
                if (stage === 'engine' || stage === 'inspect') report('engine', frac);
                else if (stage === 'remux' || stage === 'stream' || stage === 'verify') report('remux', frac);
                else if (stage === 'transcode') report('convert', frac);
                else if (stage === 'work') report('work', frac);
                else report('probe', frac);
            }
        });
        throwIfCancelled();
        if (!res || !res.ok) {
            var err = new Error('ladder: ' + (res && res.error || 'failed'));
            err.step = (res && res.error === 'unreadable') ? 'unreadable'
                : (res && (res.error === 'audio_unplayable' || (res.meta && res.meta.audioOnly))) ? 'no_video'
                : (res && res.error === 'engine_unavailable') ? 'engine'
                : (res && res.deferred) ? 'deferred' : 'ladder';
            err.meta = res && res.meta;
            throw err;
        }
        if (!res.converted) {
            // 'native' from the ladder means its own probe played the file after
            // all (e.g. our rung-1 wait timed out on a slow device) — use the pick.
            return { file: src, url: res.src, tier: res.tier, meta: res.meta || {}, cleanup: res.cleanup };
        }
        var proxy = new File([res.converted], stem(src.name) + '.mp4', { type: 'video/mp4' });
        return { file: proxy, url: res.src, tier: res.tier, meta: res.meta || {}, cleanup: res.cleanup };
    }

    async function open(file, opts) {
        opts = opts || {};
        cancelled = false;
        var t0 = performance.now();
        var timeoutMs = opts.timeoutMs || 25000;
        var report = function (stage, frac) { try { opts.onProgress && opts.onProgress(stage, frac); } catch (e) { } };
        var video = opts.video;
        var out = {
            orig: file, file: null, url: null, tier: 'native', durable: 0,
            vw: 0, vh: 0, dur: 0, vcodec: '', acodec: '', container: ext(file.name),
            extractor: '', scaledTo: 0, ms: 0, _urls: [], _cleanup: null
        };

        report('probe', 0.02);
        var src = await durable(file);
        out.durable = src !== file ? 1 : 0;

        // rung 1
        var url = URL.createObjectURL(src);
        var nativeErr = null;
        try {
            var m = await tryVideo(video, url, timeoutMs);
            out.file = src; out.url = url; out.vw = m.vw; out.vh = m.vh; out.dur = m.dur; out._urls.push(url);
        } catch (e) { nativeErr = e; revoke(url); }

        if (!nativeErr) {
            var ex = await extractorVerdict(src);
            out.extractor = ex.reason;
            if (!ex.remuxable) return finish(out, t0);
            // 2b — keep what plays, but try to earn hardware extraction with a copy.
            try {
                var rm = await ladder(src, opts, { remuxOnly: true }, report);
                throwIfCancelled();
                var m2 = await tryVideo(video, rm.url, timeoutMs);
                var ex2 = await extractorVerdict(rm.file);
                if (ex2.ok) {
                    revoke(out.url);
                    out.file = rm.file; out.url = rm.url; out.vw = m2.vw; out.vh = m2.vh; out.dur = m2.dur;
                    out.tier = 'remux-extractor'; out.extractor = 'ok'; out._cleanup = rm.cleanup;
                    fillMeta(out, rm.meta);
                    return finish(out, t0);
                }
                try { rm.cleanup && rm.cleanup(); } catch (e2) { }
            } catch (e3) {
                if (e3 && e3.step === 'cancel') throw e3;
                /* the native source is still good — say nothing, keep it */
            }
            // put the element back on the source we are keeping
            try { await tryVideo(video, out.url, timeoutMs); } catch (e4) { }
            return finish(out, t0);
        }

        // rung 2
        var got;
        try { got = await ladder(src, opts, {}, report); }
        catch (e5) {
            if (e5 && e5.step === 'cancel') throw e5;
            var err = new Error(e5 && e5.message || 'ladder failed');
            err.step = (e5 && e5.step) || 'ladder';
            err.nativeStep = nativeErr.step; err.mediaCode = nativeErr.mediaCode;
            err.rs = nativeErr.rs; err.ns = nativeErr.ns;
            fillMeta(err, e5 && e5.meta);
            throw err;
        }
        throwIfCancelled();
        try {
            var m3 = await tryVideo(video, got.url, timeoutMs);
            out.file = got.file; out.url = got.url; out.vw = m3.vw; out.vh = m3.vh; out.dur = m3.dur;
            out.tier = got.tier; out._cleanup = got.cleanup; fillMeta(out, got.meta);
        } catch (e6) {
            try { got.cleanup && got.cleanup(); } catch (e7) { }
            var err2 = new Error('proxy unplayable: ' + (e6 && e6.message));
            err2.step = 'ladder'; err2.tier = got.tier; err2.nativeStep = nativeErr.step; err2.mediaCode = nativeErr.mediaCode;
            fillMeta(err2, got.meta);
            throw err2;
        }
        var ex3 = await extractorVerdict(out.file);
        out.extractor = ex3.reason;
        return finish(out, t0);
    }

    function fillMeta(target, meta) {
        if (!meta) return;
        if (meta.codec) target.vcodec = String(meta.codec).slice(0, 16);
        if (meta.acodec) target.acodec = String(meta.acodec).slice(0, 16);
        if (meta.container) target.container = String(meta.container).slice(0, 8);
        if (meta.scaledTo) target.scaledTo = meta.scaledTo;
    }
    function finish(out, t0) {
        out.ms = Math.round(performance.now() - t0);
        return out;
    }

    function cancel() {
        cancelled = true;
        try { if (root.FFmpegProcessor && typeof root.FFmpegProcessor.cancel === 'function') root.FFmpegProcessor.cancel(); } catch (e) { }
    }

    function release(src) {
        if (!src) return;
        try { if (src._cleanup) src._cleanup(); } catch (e) { }
        (src._urls || []).forEach(revoke);
        if (src.url) revoke(src.url);
    }

    root.RoSource = { open: open, cancel: cancel, release: release, awaitMeta: awaitMeta, extractorVerdict: extractorVerdict, ensureFfmpeg: ensureFfmpeg };
})(typeof window !== 'undefined' ? window : this);
